import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { 
  Check, 
  ArrowRight, 
  Wifi, 
  Coffee, 
  Users,
  Calendar,
  Zap,
  Clock
} from "lucide-react";

const images = {
  hero: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/drhm18b5_Main%20Room%203.jpg",
  mainRoom: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/drhm18b5_Main%20Room%203.jpg",
  lobby: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/a06ekh31_Lobby.jpg",
  lobby2: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/0yum9doa_Lobby%202.jpg",
};

const membershipTypes = [
  {
    name: "Day Pass",
    description: "Perfect for occasional use",
    features: [
      "Full day access",
      "Any available desk",
      "WiFi & amenities included",
      "Conference room access (hourly)",
    ],
    highlight: false,
  },
  {
    name: "Hot Desk",
    description: "Flexible monthly membership",
    features: [
      "Unlimited access",
      "Any available desk",
      "WiFi & all amenities",
      "Conference room hours included",
      "Community events access",
    ],
    highlight: true,
  },
  {
    name: "Dedicated Desk",
    description: "Your own permanent spot",
    features: [
      "24/7 access",
      "Your own dedicated desk",
      "Dedicated workspace included",
      "Conference room hours",
      "Mail handling",
      "Priority booking",
    ],
    highlight: false,
  },
];

const amenities = [
  { icon: Wifi, title: "Super-Fast WiFi", description: "Enterprise-grade internet" },
  { icon: Coffee, title: "Coffee & Kitchen", description: "Gourmet coffee bar included" },
  { icon: Users, title: "Community", description: "Networking & events" },
  { icon: Calendar, title: "Flexible Terms", description: "No long-term contracts" },
  { icon: Zap, title: "Productive Space", description: "Designed for focus" },
  { icon: Clock, title: "Extended Hours", description: "Work on your schedule" },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const CoworkingPage = () => {
  return (
    <div className="pt-20" data-testid="coworking-page">
      <SEO
        title="Coworking Space Brevard County FL | Hot Desks & Dedicated Desks in Satellite Beach"
        description="Flexible coworking memberships in Brevard County. Hot desks, dedicated desks, and day passes in Satellite Beach for remote workers, freelancers and entrepreneurs. Ocean views included."
        canonical="https://www.workcoastal.com/coworking"
      />
      {/* Hero Section */}
      <section className="relative py-32 bg-brand overflow-hidden" data-testid="coworking-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.hero}
            alt="Coworking Space"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-sans text-sm font-medium text-sea-light uppercase tracking-wider mb-4">
              Coworking Brevard County Florida
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Coworking Space
              <span className="block text-sand-light">Satellite Beach & Indialantic</span>
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed mb-8">
              Flexible coworking memberships in Brevard County. Hot desks, dedicated desks, and day passes 
              for remote workers, freelancers, and entrepreneurs in Satellite Beach and Indialantic.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-sea hover:bg-sea-light text-white rounded-full px-8 btn-primary"
                data-testid="coworking-hero-cta"
              >
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Membership Options */}
      <section className="py-24 bg-white" data-testid="coworking-memberships">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-16"
            {...fadeUp}
          >
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Flexible Membership Options
            </h2>
            <p className="font-sans text-muted-foreground leading-relaxed">
              Choose the membership that fits your work style. All memberships include access 
              to our amenities and community.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {membershipTypes.map((membership, index) => (
              <motion.div
                key={membership.name}
                className={`rounded-2xl p-8 ${
                  membership.highlight 
                    ? 'bg-brand text-white ring-4 ring-sea/30' 
                    : 'bg-sand-light/30 border border-sand/30'
                } card-hover`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`membership-${membership.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {membership.highlight && (
                  <span className="inline-block font-sans text-xs font-medium uppercase tracking-wider text-sea-light mb-4">
                    Most Popular
                  </span>
                )}
                <h3 className={`font-serif text-2xl font-bold mb-2 ${
                  membership.highlight ? 'text-white' : 'text-brand'
                }`}>
                  {membership.name}
                </h3>
                <p className={`font-sans text-sm mb-6 ${
                  membership.highlight ? 'text-white/70' : 'text-muted-foreground'
                }`}>
                  {membership.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {membership.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        membership.highlight ? 'text-sea-light' : 'text-sea'
                      }`} strokeWidth={2} />
                      <span className={`font-sans text-sm ${
                        membership.highlight ? 'text-white/90' : 'text-muted-foreground'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button 
                    className={`w-full rounded-full ${
                      membership.highlight 
                        ? 'bg-white text-brand hover:bg-sand-light' 
                        : 'bg-brand text-white hover:bg-brand-light'
                    }`}
                    data-testid={`membership-cta-${membership.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    Inquire Now
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery & Benefits */}
      <section className="py-24 bg-sand-light/30" data-testid="coworking-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl font-bold text-brand mb-6">
                More Than Just a Desk in Brevard County
              </h2>
              <p className="font-sans text-muted-foreground leading-relaxed mb-6">
                At Coastal Collaborative, we've created the premier coworking space in Satellite Beach. 
                With ocean views, natural lighting, and a supportive community, you'll find 
                the perfect environment to do your best work in Brevard County Florida.
              </p>
              <p className="font-sans text-muted-foreground leading-relaxed mb-8">
                Our coworking space features 16 desks in our open area, perfect for 
                freelancers, remote workers, and small teams from Indialantic, Melbourne Beach, 
                and throughout Brevard County who want flexibility without isolation.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <amenity.icon className="w-5 h-5 text-sea" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="font-sans text-sm font-semibold text-brand">{amenity.title}</h4>
                      <p className="font-sans text-xs text-muted-foreground">{amenity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={images.mainRoom}
                alt="Coworking Main Room"
                className="rounded-2xl image-hover w-full aspect-[3/4] object-cover"
              />
              <img
                src={images.lobby}
                alt="Coworking Lobby"
                className="rounded-2xl image-hover w-full aspect-[3/4] object-cover mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white" data-testid="coworking-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Try Coworking Today
            </h2>
            <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-8">
              Start with a day pass or schedule a tour to see if Coastal Collaborative 
              is the right fit for your work style.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-brand text-white hover:bg-brand-light rounded-full px-8 btn-primary"
                data-testid="coworking-cta-button"
              >
                Request a Tour
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CoworkingPage;
